import * as React from 'react';
import Modal from 'react-modal';

import DeviceSettingsItemTemplate from './DeviceSettingsItemTemplate';
import { dispatcher } from 'lib/Dispatcher';
import { PushEvents } from 'pusher/PusherConstants';
import Link from 'components/Link';
import Text from 'components/Text';
// @ts-ignore
import WebNotificationActions from 'notifications/WebNotificationActions';
// @ts-ignore
import WebNotificationStore from 'notifications/WebNotificationStore';
// @ts-ignore
import WebNotificationTutorialModal from 'notifications/WebNotificationTutorialModal';

type State = {
  tutorialModalIsOpen: boolean;
  areNotificationsUserEnabled: boolean;
};

/* Desktop notifications are a little funky because the UI that we show
 * depends on two sets of permissions: one from the browser and one from
 * the user.
 *
 * If the browser tells us that it does not have permission to
 * send desktop notifications, we simply do not show any actions for the
 * user to take.
 *
 * If the browser tells us that it hasn't yet received prompting as to
 * whether or not it has permission to send notifications, we show a button
 * that launches the browser permissioning flow.
 *
 * If the browser tells us that it has been granted permission to send
 * notifications, we show an On/Off toggle, which then depends on the
 * permission that comes from the user (which we read from
 * the WebNotificationStore).
 */

export default class DeviceSettingsDesktopItem extends React.Component<{}, State> {
  state = {
    tutorialModalIsOpen: false,
    areNotificationsUserEnabled: WebNotificationStore.areNotificationsUserEnabled(),
  };

  showTutorialModal = () => {
    this.setState({ tutorialModalIsOpen: true });
  };

  hideTutorialModal = () => {
    this.setState({ tutorialModalIsOpen: false });
  };

  sendTestWebNotification = () => {
    dispatcher.dispatch({
      type: PushEvents.TEST_MESSAGE,
    });
  };

  updatePermissionState = () => {
    const areNotificationsUserEnabled = WebNotificationStore.areNotificationsUserEnabled();
    this.setState({ areNotificationsUserEnabled });
  };

  renderSecondaryText = () => {
    const browserPermission = WebNotificationStore.getBrowserPermission();
    if (browserPermission === 'granted') {
      return (
        <Link
          onClick={this.sendTestWebNotification}
          disabled={!this.state.areNotificationsUserEnabled}>
          Send test notification
        </Link>
      );
    } else if (browserPermission === 'denied') {
      return (
        <Text color="apple">
          Re-enable desktop notifications for remind.com in your browser’s settings.
        </Text>
      );
    } else {
      return 'Browser notifications';
    }
  };

  render() {
    const { areNotificationsUserEnabled } = this.state;
    const browserPermission = WebNotificationStore.getBrowserPermission();
    const browserPermissionWasGranted = browserPermission === 'granted';
    const browserPermissionWasDefined =
      browserPermission === 'granted' || browserPermission === 'denied';
    const desktopNotificationsAreEnabled =
      browserPermissionWasGranted && areNotificationsUserEnabled;
    const toggleDesktopNotifications = () => {
      WebNotificationActions.updateLocalUserEnabled(!areNotificationsUserEnabled);
      this.updatePermissionState();
    };

    return (
      <React.Fragment>
        <Modal isOpen={this.state.tutorialModalIsOpen}>
          <WebNotificationTutorialModal
            onRequestHide={() => {
              this.updatePermissionState();
              this.hideTutorialModal();
            }}
          />
        </Modal>
        <DeviceSettingsItemTemplate
          deviceType="desktop"
          deviceExists={browserPermissionWasDefined}
          actionForNonexistentDevice={{
            label: 'Enable',
            action: this.showTutorialModal,
          }}
          deviceIsEnabled={desktopNotificationsAreEnabled}
          deviceCanBeEnabledOrDisabled={browserPermissionWasGranted}
          primaryTextLabel="Desktop"
          secondaryTextLabel={this.renderSecondaryText()}
          onEnableOrDisableDevice={toggleDesktopNotifications}
        />
      </React.Fragment>
    );
  }
}
